import { useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { calcDistance } from "../utils/distance";

export interface RouteStop {
  id: string;
  venueId: string;
  venueName: string;
  category: string;
  categorySlug: string;
  address: string;
  district: string;
  image: string;
  lat: number;
  lng: number;
  completed: boolean;
  completedAt?: string;
  orderIndex: number;
}

export interface UserRoute {
  id: string;
  name: string;
  userId: string;
  stops: RouteStop[];
  createdAt: string;
  isTracking: boolean;
  startedAt?: string;
  partnerUserId?: string | null;
  partnerRouteId?: string | null;
}

function mapStop(row: Record<string, unknown>): RouteStop {
  return {
    id: row.id as string,
    venueId: row.venue_id as string,
    venueName: row.venue_name as string,
    category: row.category as string,
    categorySlug: row.category_slug as string,
    address: row.address as string,
    district: row.district as string,
    image: row.image as string,
    lat: row.lat as number,
    lng: row.lng as number,
    completed: row.completed as boolean,
    completedAt: row.completed_at as string | undefined,
    orderIndex: row.order_index as number,
  };
}

function mapRoute(
  row: Record<string, unknown>,
  stops: RouteStop[],
): UserRoute {
  return {
    id: row.id as string,
    name: row.name as string,
    userId: row.user_id as string,
    stops: stops.sort((a, b) => a.orderIndex - b.orderIndex),
    createdAt: row.created_at as string,
    isTracking: row.is_tracking as boolean,
    startedAt: row.started_at as string | undefined,
    partnerUserId: (row.partner_user_id as string | null) ?? null,
    partnerRouteId: (row.partner_route_id as string | null) ?? null,
  };
}

export function useMyRoutes(userId: string | undefined) {
  const [routes, setRoutes] = useState<UserRoute[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRoutes = useCallback(async () => {
    if (!userId) {
      setRoutes([]);
      return;
    }
    setLoading(true);
    const { data: routeRows, error } = await supabase
      .from("user_routes")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error || !routeRows) {
      setLoading(false);
      return;
    }

    if (routeRows.length === 0) {
      setRoutes([]);
      setLoading(false);
      return;
    }

    const routeIds = routeRows.map((r) => r.id as string);
    const { data: stopRows } = await supabase
      .from("route_stops")
      .select("*")
      .in("route_id", routeIds)
      .order("order_index", { ascending: true });

    const stopsMap: Record<string, RouteStop[]> = {};
    (stopRows ?? []).forEach((s: Record<string, unknown>) => {
      const rid = s.route_id as string;
      if (!stopsMap[rid]) stopsMap[rid] = [];
      stopsMap[rid].push(mapStop(s));
    });

    setRoutes(routeRows.map((r: Record<string, unknown>) => mapRoute(r, stopsMap[r.id as string] ?? [])));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  const createRoute = useCallback(
    async (
      name: string,
      stops: Omit<RouteStop, "id" | "completed" | "completedAt" | "orderIndex">[],
    ) => {
      if (!userId) return;

      const { data: routeRow, error } = await supabase
        .from("user_routes")
        .insert({ user_id: userId, name, is_tracking: false })
        .select()
        .maybeSingle();

      if (error || !routeRow) return;

      const stopInserts = stops.map((s, idx) => ({
        route_id: routeRow.id,
        venue_id: s.venueId,
        venue_name: s.venueName,
        category: s.category,
        category_slug: s.categorySlug,
        address: s.address,
        district: s.district,
        image: s.image,
        lat: s.lat,
        lng: s.lng,
        completed: false,
        order_index: idx,
      }));

      if (stopInserts.length > 0) {
        await supabase.from("route_stops").insert(stopInserts);
      }

      await fetchRoutes();
    },
    [userId, fetchRoutes],
  );

  const deleteRoute = useCallback(
    async (routeId: string) => {
      await supabase.from("user_routes").delete().eq("id", routeId);
      setRoutes((prev) => prev.filter((r) => r.id !== routeId));
    },
    [],
  );

  const startTracking = useCallback(
    async (routeId: string, userLat: number, userLng: number) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      const sorted = [...route.stops].sort(
        (a, b) =>
          calcDistance(userLat, userLng, a.lat, a.lng) -
          calcDistance(userLat, userLng, b.lat, b.lng),
      );

      // Update order_index for each stop
      for (let i = 0; i < sorted.length; i++) {
        await supabase
          .from("route_stops")
          .update({ order_index: i })
          .eq("id", sorted[i].id);
      }

      await supabase
        .from("user_routes")
        .update({ is_tracking: true, started_at: new Date().toISOString() })
        .eq("id", routeId);

      await fetchRoutes();
    },
    [routes, fetchRoutes],
  );

  const stopTracking = useCallback(
    async (routeId: string) => {
      await supabase
        .from("user_routes")
        .update({ is_tracking: false })
        .eq("id", routeId);
      setRoutes((prev) =>
        prev.map((r) => (r.id !== routeId ? r : { ...r, isTracking: false })),
      );
    },
    [],
  );

  const checkAndCompleteStop = useCallback(
    async (routeId: string, venueId: string, userLat: number, userLng: number) => {
      const route = routes.find((r) => r.id === routeId);
      if (!route) return false;
      const stop = route.stops.find((s) => s.venueId === venueId);
      if (!stop || stop.completed) return false;
      const dist = calcDistance(userLat, userLng, stop.lat, stop.lng);
      if (dist <= 500) {
        const completedAt = new Date().toISOString();
        await supabase
          .from("route_stops")
          .update({ completed: true, completed_at: completedAt })
          .eq("id", stop.id);
        setRoutes((prev) =>
          prev.map((r) =>
            r.id !== routeId
              ? r
              : {
                  ...r,
                  stops: r.stops.map((s) =>
                    s.venueId !== venueId
                      ? s
                      : { ...s, completed: true, completedAt },
                  ),
                },
          ),
        );
        return true;
      }
      return false;
    },
    [routes],
  );

  return {
    routes,
    loading,
    createRoute,
    deleteRoute,
    startTracking,
    stopTracking,
    checkAndCompleteStop,
    refetch: fetchRoutes,
  };
}
